import { memo, useCallback } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { ErrorHandler } from '@components/CartDrawer/ErrorHandler/ErrorHandler'
import {
  type TransformedProductWithRawImages,
  transformProductToWishlistLineItem,
} from '@components/WishlistLineItem/transformProductToWishlistLineItem'
import { WishlistLineItem } from '@components/WishlistLineItem/WishlistLineItem'
import { useWishlistProducts } from '@components/WishlistProvider/useWishlistProducts'
import type { WishlistProductData } from '@components/WishlistProvider/Wishlist.types'
import { useWishlist } from '@components/WishlistProvider/WishlistProvider'
import type { WishlistItem } from '@velasca/types'
import { Button } from '@velasca/ui/button'
import { Drawer } from '@velasca/ui/drawer'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { getIdFromShopifyGid } from '@velasca/utils/get-id-from-shopify-gid'
import * as S from './WishlistDrawer.styles'

// Check if user is a guest (not logged in)
const isGuestUser = (): boolean => {
  return typeof window !== 'undefined' && window.customerID === 'unknown'
}

// Memoized line item to prevent unnecessary re-renders
const WishlistItemComponent = ({
  product,
  originalWishlistItem,
  removeProduct,
}: {
  product: TransformedProductWithRawImages
  originalWishlistItem: WishlistItem | undefined
  removeProduct: (data: WishlistProductData) => Promise<void>
}) => {
  const lineItemProps = transformProductToWishlistLineItem(product)

  // Derive UI hints from Swym cprops
  const variantSelectedByUser =
    originalWishlistItem?.cprops?.variantSelectedByUser === true

  // Try to compute the human size label from the selected variant id
  let preselectedSizeLabel: string | undefined

  if (variantSelectedByUser) {
    const epiStr = String(originalWishlistItem?.epi ?? '')

    preselectedSizeLabel =
      lineItemProps.sizes?.find((s) => String(s.id) === epiStr)?.name ||
      preselectedSizeLabel
  }

  const handleRemove = useCallback(() => {
    if (!originalWishlistItem) {
      console.error('Could not find original wishlist item for removal')

      return
    }

    // Provider handles the optimistic update
    removeProduct({
      handle: product.handle,
      variantId: `gid://shopify/ProductVariant/${originalWishlistItem.epi}`,
      productId: product.id,
    }).catch((error) => {
      console.error('Failed to remove product from wishlist:', error)
    })
  }, [originalWishlistItem, product.handle, product.id, removeProduct])

  return (
    <WishlistLineItem
      onRemove={handleRemove}
      {...lineItemProps}
      preselectedSizeLabel={preselectedSizeLabel}
    />
  )
}

// Custom comparison function for memo - only re-render if product ID, originalWishlistItem, or removeProduct changes
const arePropsEqual = (
  prevProps: {
    product: TransformedProductWithRawImages
    originalWishlistItem: WishlistItem | undefined
    removeProduct: (data: WishlistProductData) => Promise<void>
  },
  nextProps: {
    product: TransformedProductWithRawImages
    originalWishlistItem: WishlistItem | undefined
    removeProduct: (data: WishlistProductData) => Promise<void>
  },
) => {
  // If product is missing in either props, force re-render (this indicates a data inconsistency)
  if (!prevProps.product || !nextProps.product) {
    return false
  }

  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.originalWishlistItem?.empi ===
      nextProps.originalWishlistItem?.empi &&
    prevProps.originalWishlistItem?.epi ===
      nextProps.originalWishlistItem?.epi &&
    prevProps.removeProduct === nextProps.removeProduct
  )
}

const MemoizedWishlistItem = memo(WishlistItemComponent, arePropsEqual)

export const WishlistDrawer = () => {
  const { t } = useTranslation('global')
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    wishlistItems: providerWishlistItems,
    removeProduct,
  } = useWishlist()

  // Fetch rich product data from Shopify
  const {
    loading: productsLoading,
    wishlistProducts,
    error: productsError,
  } = useWishlistProducts(providerWishlistItems, !isDrawerOpen)

  return (
    <>
      <S.WishlistDrawerGlobalStyles />
      <Drawer
        className="wishlist-drawer"
        isOpen={isDrawerOpen}
        aria-label={t('wishlistModalTitle')}
        close={() => {
          setIsDrawerOpen(false)
        }}
      >
        <Drawer.Header
          title={t('wishlistModalTitle')}
          onClose={() => {
            setIsDrawerOpen(false)
          }}
        />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ErrorHandler>
            <Drawer.Content
              isLoading={productsLoading}
              className="wishlist-drawer-content"
              aria-live="polite"
              aria-busy={productsLoading}
            >
              <S.ContentContainer>
                {isEmpty(providerWishlistItems) ? (
                  <S.EmptyMessage>{t('wishlistEmpty')}</S.EmptyMessage>
                ) : isEmpty(wishlistProducts) && !productsLoading ? (
                  <S.EmptyMessage>{t('wishlistEmpty')}</S.EmptyMessage>
                ) : (
                  <>
                    <S.WishlistGrid
                      role="list"
                      aria-label={`Wishlist items, ${wishlistProducts.length} item${wishlistProducts.length !== 1 ? 's' : ''}`}
                    >
                      {wishlistProducts.map((product) => {
                        // Find the original wishlist item that matches this product
                        const originalWishlistItem = providerWishlistItems.find(
                          (item) => {
                            const productIdFromGid = getIdFromShopifyGid(
                              product.id,
                            )

                            return item.empi === Number(productIdFromGid)
                          },
                        )

                        return (
                          <MemoizedWishlistItem
                            key={product.id || product.handle}
                            product={product}
                            originalWishlistItem={originalWishlistItem}
                            removeProduct={removeProduct}
                          />
                        )
                      })}
                      {productsError && (
                        <S.ErrorMessage>
                          Error loading products: {productsError.message}
                        </S.ErrorMessage>
                      )}
                    </S.WishlistGrid>
                    {isGuestUser() && (
                      <S.Actions
                        role="region"
                        aria-label="Account actions"
                        aria-describedby="guest-user-message"
                      >
                        <div id="guest-user-message" className="sr-only">
                          Create an account to save your wishlist items across
                          devices
                        </div>
                        <Button
                          fullWidth
                          as="a"
                          href="/account/login"
                          variant="primary"
                          text={t('wishlistLoginButton')}
                          aria-describedby="login-description"
                        />
                        <div id="login-description" className="sr-only">
                          Sign in to access your saved wishlist
                        </div>
                        <Button
                          fullWidth
                          as="a"
                          href="/account/register"
                          variant="ghost"
                          text={t('wishlistRegisterButton')}
                          aria-describedby="register-description"
                        />
                        <div id="register-description" className="sr-only">
                          Create a new account to start saving items
                        </div>
                      </S.Actions>
                    )}
                  </>
                )}
              </S.ContentContainer>
            </Drawer.Content>
          </ErrorHandler>
        </ErrorBoundary>
      </Drawer>
    </>
  )
}
